/**
 * Copyright (c) 2010-2024 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import {
    AfterContentInit,
    Component,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { LoggerService } from '../../../shared/services/logger.service';
import { distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { groupBy, isEqual } from 'lodash-es';
import { IsolatedReportService } from '../../services/isolatedReport.service';
import { ReportContentBaseComponent } from '../report-content-base.component';
import { CustomPieChartComponent } from './custom-pie-chart.component';
import { BarVerticalComponent, DataItem, LineChartComponent } from '@swimlane/ngx-charts';
import { Subject } from 'rxjs';
import { CustomBarVerticalComponent } from './custom-bar-vertical.component';
import { ReportService } from '../../services/report.service';
import { ReportFilterService } from '../../services/report-filter.service';
import { ReportLinkField } from '../../state/report.model';

@Component({
    selector: 'dash-gis-report-content-chart',
    templateUrl: './report-content-chart.component.html',
    styleUrls: ['./report-content-chart.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class ReportContentChartComponent
    extends ReportContentBaseComponent
    implements AfterContentInit, OnChanges, OnDestroy, OnInit
{
    public height: number;
    public width: number;

    @Input()
    isPrinting = false;

    @ViewChild(CustomPieChartComponent)
    private pieChart: CustomPieChartComponent;

    @ViewChild(CustomBarVerticalComponent)
    private barChart: CustomBarVerticalComponent;

    @ViewChild(LineChartComponent)
    private lineChart: LineChartComponent;

    private tickLabels = new Map<string, string>();
    private destroySubject = new Subject<void>();

    constructor(
        logger: LoggerService,
        isolatedReport: IsolatedReportService,
        private reportService: ReportService,
        private filterService: ReportFilterService
    ) {
        super(logger, isolatedReport);
    }

    ngAfterContentInit(): void {}

    ngOnChanges(changes: SimpleChanges): void {
        let hasChanged = false;
        if (changes.definition && changes.definition.currentValue) {
            if (!isEqual(changes.definition.currentValue, changes.definition.previousValue)) {
                hasChanged = true;
            }
        }

        if (changes.dataSource && changes.dataSource.currentValue) {
            if (!isEqual(changes.dataSource.currentValue, changes.dataSource.previousValue)) {
                hasChanged = true;
            }
        }

        if (hasChanged) {
            this.dataSource.loadAllReportData();
        }
    }

    ngOnDestroy() {
        this.destroySubject.next();
    }

    ngOnInit() {
        this.data$.pipe(takeUntil(this.destroySubject.asObservable())).subscribe((data) => {
            if (data && this.config.tickLabelField) {
                data.forEach((row) => {
                    this.tickLabels.set(row.name, row[this.config.tickLabelField]);
                });
            }
        });
    }

    @HostListener('window:beforeprint', ['$event'])
    async beforePrint() {
        const promise = new Promise<void>((resolve) => {
            if (this.isPrinting) {
                const chart = this.getChildChart();
                chart.update();
            }

            setTimeout(() => resolve(), 1000);
        });

        await promise;
    }

    formatLabels(name: string, index: number, data?: any) {
        return !!data ? `${name}: ${data.value}` : name;
    }

    getXAxisTickFormatter() {
        return (tick) => {
            return this.tickLabels.has(tick) ? this.tickLabels.get(tick) : tick;
        };
    }

    // xAxisTicks(data: any[]) {
    //     return data.map((x) => ({ ...x, ...{ name: `XXX${x.name}XXX` } }));
    // }

    onChartClick(data) {
        const clickReport = this.definition.chartReport;
        if (clickReport) {
            const reportFilter = this.filterService.getFilterValue(
                clickReport.filterType,
                clickReport.filterField,
                clickReport.filterValue
            );
            this.reportService.runReportByName({
                name: clickReport.name,
                key: null,
                filter: reportFilter,
            });
        }
    }

    onSeriesClick(data: DataItem) {
        const clickReport = this.definition.fields.find((field) => field.type === 'report') as ReportLinkField;
        if (clickReport) {
            const reportFilter = this.filterService.getFilterValue(
                clickReport.filterType,
                clickReport.filterField,
                data[clickReport.filterValue]
            );
            this.reportService.runReportByName({
                name: clickReport.reportName,
                key: clickReport.reportKey,
                filter: reportFilter,
            });
        }
    }

    private getChildChart(): CustomPieChartComponent | BarVerticalComponent | LineChartComponent {
        switch (this.definition.chartConfig.type) {
            case 'pie':
                return this.pieChart;
            case 'bar':
                return this.barChart;
            case 'line':
                return this.lineChart;
        }
    }

    get dimensions$() {
        return this.isolatedReportService.contentDimension$.pipe(
            distinctUntilChanged(isEqual),
            map((dim: [number, number]) => [Math.max(0, dim[0]), Math.max(0, dim[1] - 40)])
        );
    }

    get config() {
        return this.definition.chartConfig;
    }

    get data$() {
        return this.dataSource.reportAllRawData$.pipe(
            map((data) => {
                if (data && this.definition.chartConfig.type === 'line') {
                    const grouped = groupBy(data, 'series');
                    return Object.getOwnPropertyNames(grouped).reduce((acc, k) => {
                        acc.push({
                            name: k,
                            series: grouped[k],
                        });
                        return acc;
                    }, []);
                }
                return data;
            })
        );
    }
}
